import { inject, Injectable } from '@angular/core';
import { HttpInterceptor, HttpRequest, HttpHandler, HttpEvent } from '@angular/common/http';
import { from, mergeMap, Observable } from 'rxjs';
import { getAuth, User } from 'firebase/auth';
import { OperatorService } from '../services/operator/operator.service';
import { SignedOutError } from 'src/app/shared/errors/signed-out-error';
import { DEALER_ENDPOINTS } from '../api/dealer/dealer-api';
import { AnalyticsService } from '../services/analytics/analytics.service';
import { JIT_ENDPOINTS } from '../api/jit/jit-api';
import { TOURNAMENT_ENDPOINTS, WAGER_ENDPOINTS } from '../api/player/player-api';
import {
  isFirebaseNetworkError,
  extractFirebaseErrorDetails,
} from 'src/app/shared/errors/error-utilities';
import { getNetworkDiagnostics } from 'src/app/shared/utils/network.utils';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private analyticsService = inject(AnalyticsService);

  private readonly authenticatedUrls = [
    DEALER_ENDPOINTS.CHECKPOINTS,
    DEALER_ENDPOINTS.GAME_STATES,
    DEALER_ENDPOINTS.REPLAYS,
    JIT_ENDPOINTS.SET_LIGHTNING_ADDRESS,
    JIT_ENDPOINTS.CREATE_INVOICE,
    JIT_ENDPOINTS.DEPOSIT_STATUS,
    WAGER_ENDPOINTS.ACTIVITY_FEED,
    WAGER_ENDPOINTS.FIND_MATCH,
    WAGER_ENDPOINTS.FINISH_ROUND,
    WAGER_ENDPOINTS.GAME_LEADERBOARDS,
    WAGER_ENDPOINTS.PRACTICE_ROUNDS,
    WAGER_ENDPOINTS.GAME_STATS,
    WAGER_ENDPOINTS.START_ROUND,
    WAGER_ENDPOINTS.TIERS,
    WAGER_ENDPOINTS.ROOMS,
    WAGER_ENDPOINTS.WAITING_OPPONENTS,
    WAGER_ENDPOINTS.BALANCE,
    WAGER_ENDPOINTS.START_TOURNAMENT_ENTRY,
    WAGER_ENDPOINTS.LOG_CLIENT_EVENT,
    TOURNAMENT_ENDPOINTS.TOURNAMENTS,
  ];

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (OperatorService.IsDemo || !this.requiresAuth(request.url)) {
      return next.handle(request);
    }

    return from(this.tryGetUser(request.url)).pipe(
      mergeMap((user) => from(this.tryGetToken(user, request.url))),
      mergeMap((token) => {
        const authRequest = token
          ? request.clone({
              setHeaders: { Authorization: `Bearer ${token}` },
            })
          : request;
        return next.handle(authRequest);
      }),
    );
  }

  private requiresAuth(url: string): boolean {
    return this.authenticatedUrls.some((endpoint) => url.includes(endpoint));
  }

  private async tryGetUser(url: string): Promise<User> {
    const auth = getAuth();

    for (let attempt = 0; attempt < 3; attempt++) {
      const user = auth.currentUser;
      if (user) {
        return user;
      }

      this.analyticsService.logBreadcrumb(`auth.interceptor: Failed get user, attempt: ${attempt}`);

      const delay = 100 * (1 << attempt);
      await this.sleep(delay);
    }

    // Last attempt after backoff loop
    const finalUser = auth.currentUser;
    if (finalUser) {
      return finalUser;
    }
    this.analyticsService.logBreadcrumb(`auth.interceptor: User signed out from: ${url}`);
    throw new SignedOutError(`User signed out from: ${url}`, url);
  }

  private async tryGetToken(user: User, url: string): Promise<string> {
    const retryDelays = [500, 1000, 3000];
    const startTime = Date.now();

    for (let attempt = 0; attempt <= retryDelays.length; attempt++) {
      const attemptStartTime = Date.now();
      try {
        const tokenResult = await user.getIdTokenResult();
        const expiresAt = new Date(tokenResult.expirationTime).getTime();
        const timeRemaining = (expiresAt - Date.now()) / 1000 / 60;
        this.analyticsService.logBreadcrumb(
          `auth.interceptor: Get IdToken: ${tokenResult.issuedAtTime}, remaining: ${timeRemaining}, url: ${url}`,
        );
        return tokenResult.token;
      } catch (error) {
        if (!isFirebaseNetworkError(error)) {
          throw error;
        }

        const attemptDurationMs = Date.now() - attemptStartTime;
        const totalDurationMs = Date.now() - startTime;
        const networkState = getNetworkDiagnostics();
        const firebaseInfo = extractFirebaseErrorDetails(error);
        const attemptInfo = `attempt ${attempt + 1}/${retryDelays.length + 1}`;
        const timingInfo = `duration=${attemptDurationMs}ms, total=${totalDurationMs}ms`;

        if (attempt < retryDelays.length) {
          const delayMs = retryDelays[attempt];
          this.analyticsService.logBreadcrumb(
            `auth.interceptor: Network error on getIdToken (${attemptInfo}): ${timingInfo}, ${networkState}, ${firebaseInfo}, url: ${url}, retrying in ${delayMs}ms`,
          );
          await this.sleep(delayMs);
        } else {
          this.analyticsService.logBreadcrumb(
            `auth.interceptor: Network error on getIdToken (${attemptInfo}): ${timingInfo}, ${networkState}, ${firebaseInfo}, url: ${url}, all retries exhausted`,
          );
          throw error;
        }
      }
    }

    throw new Error('auth.interceptor: unreachable - all retry attempts should have thrown');
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}
